import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  useTheme,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { tokens } from "../../theme";
import useRectorCommentService from "../../api/services/RectorCommentService";
import useStudentService from "../../api/services/studentService";
import useTermService from "../../api/services/termService";
import Header from "../../components/Header";
import RectorCommentsDialog from "./RectorsMessageDialog";
import EditDialog from "./EditDialog";
import DeleteDialog from "./DeleteDialog";
import { getUserFromCookies } from "../../utils/Cookie-utils";

const StudentRectorComments = () => {     
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { studentId } = useParams();

  // Get user role from cookies
  const user = getUserFromCookies();
  const userRole = user?.data?.role;
  const isAdmin = userRole === 'ADMIN';

  const { getRectorCommentsByStudent, deleteRectorCommentById, isLoading: isServiceLoading } = useRectorCommentService();
  const { loadStudentDetails } = useStudentService();
  const { fetchAllActiveTerms, terms } = useTermService();

  const [rectorComments, setRectorComments] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [deletingRow, setDeletingRow] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Fetch student info
  const loadStudent = async () => {
    try {
      const data = await loadStudentDetails(studentId);
      setSelectedStudent(data);
    } catch (err) {
      toast.error("Failed to fetch student information");
    }
  };

  // Fetch rector comments
  const loadRectorComments = async () => {
    try {
      setLoading(true);
      const data = await getRectorCommentsByStudent(studentId);
      // Ensure we have an array and properly format the data
      if (Array.isArray(data)) {
        setRectorComments(data);
      } else if (data && data.data) {
        setRectorComments(Array.isArray(data.data) ? data.data : []);
      } else {
        setRectorComments([]);
      }
    } catch (error) {
      console.error("Error loading rector comments:", error);
      if (error.response?.status !== 404) {
        toast.error("Failed to fetch rector comments: " + (error.response?.data?.message || error.message));
      } else {
        // No comments found, that's okay
        setRectorComments([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (studentId) {
      loadStudent();
      loadRectorComments();
      fetchAllActiveTerms();
    }
  }, [studentId]);

  // Edit handler
  const handleEditRow = (rowData) => {
    setEditingRow(rowData);
    setEditDialogOpen(true);
  };

  // Delete handler
  const handleDeleteRow = (rowData) => {
    setDeletingRow(rowData);
    setDeleteDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (deletingRow && deletingRow.originalData) {
      const commentId = deletingRow.originalData.id;
      if (!commentId) {
        toast.error("Cannot delete: Comment ID is missing");
        setDeleteDialogOpen(false);
        setDeletingRow(null);
        return;
      }

      try {
        await deleteRectorCommentById(commentId);
        toast.success("Rector comment deleted successfully");
        setDeleteDialogOpen(false);
        setDeletingRow(null);
        // Reload comments after deletion
        await loadRectorComments();
      } catch (error) {
        console.error("Error deleting rector comment:", error);
        toast.error("Failed to delete rector comment: " + (error.response?.data?.message || error.message));
      }
    }
  };

  // DataGrid columns
  const columns = useMemo(
    () => [
      {
        field: "termName",
        headerName: "Term",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "rectorComments",
        headerName: "Rector Comments",
        flex: 3,
        minWidth: 400,
        renderCell: (params) => (
          <Box
            sx={{
              whiteSpace: "normal",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
            }}
            dangerouslySetInnerHTML={{
              __html:
                params.value?.length > 1000
                  ? params.value.substring(0, 1000) + "..."
                  : params.value || "",
            }}
          />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: isAdmin ? 120 : 60,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Edit Rector Comment">
              <IconButton
                size="small"
                onClick={() => handleEditRow(params.row)}
                color="primary"
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            
            {/* Only show delete button for ADMIN users */}
            {isAdmin && (
              <Tooltip title="Delete Rector Comment">
                <IconButton
                  size="small"
                  onClick={() => handleDeleteRow(params.row)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      }
    ],
    [isAdmin]
  );

  // Prepare rows - properly extract data from the response
  const rows = useMemo(() => {
    if (!rectorComments || !Array.isArray(rectorComments)) {
      return [];
    }
    
    return rectorComments.map((item, index) => {
      // Handle different response structures - the backend returns data in different formats
      const termName = item.term?.termName || item.termName || "N/A";
      const rectorCommentsText = item.rectorComments || "";
      const commentId = item.id || `temp-${index}`;
      
      return {
        id: commentId,
        termName: termName,
        rectorComments: rectorCommentsText,
        originalData: {
          ...item,
          id: commentId,
          termId: item.termId || item.term?.id,
          studentId: item.studentId || item.student?.id,
        },
      };
    });
  }, [rectorComments]);

  return (
    <Box m="20px">
      <Header
        title="RECTOR'S COMMENTS"
        subtitle="View and manage Rector's remarks per term"
      />

      {/* Header actions */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5" color={colors.grey[100]}>
          Student: {selectedStudent?.studentName || "Loading..."}
          {isAdmin && (
            <Typography 
              variant="caption" 
              sx={{ 
                ml: 1, 
                color: colors.greenAccent[500],
                fontWeight: 'bold'
              }}
            >
              (Admin Mode)
            </Typography>
          )}
        </Typography>
        <Button
          color="secondary"
          variant="contained"
          onClick={() => setDialogOpen(true)}
          disabled={!selectedStudent || !terms || terms.length === 0}
        >
          Add Term's Rector Comments
        </Button>
      </Box>

      {/* Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress color="secondary" />
        </Box>
      ) : rows.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
          sx={{
            backgroundColor: colors.primary[400],
            borderRadius: 2,
            p: 4,
          }}
        >
          <Typography variant="h6" color={colors.grey[300]} gutterBottom>
            No Rector Comments Found
          </Typography>
          <Typography variant="body2" color={colors.grey[400]} sx={{ mb: 2 }}>
            Click "Add / Update Rector Comment" to add a new comment for this student.
          </Typography>
        </Box>
      ) : (
        <Box
          height="70vh"
          sx={{
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor:
                theme.palette.mode === "light"
                  ? colors.blueAccent[100]
                  : colors.blueAccent[700],
              color: theme.palette.mode === "light" ? "#000" : "#fff",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
          />
        </Box>
      )}

      {/* Add Rector Comment Dialog */}
      {dialogOpen && selectedStudent && (
        <RectorCommentsDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
          }}
          onSuccess={() => {
            setDialogOpen(false);
            setTimeout(() => {
              loadRectorComments();
            }, 500);
          }}
          students={[selectedStudent]}
          terms={terms || []}
          selectedStudent={selectedStudent}
        />
      )}

      {/* Edit Dialog */}
      {editDialogOpen && editingRow && (
        <EditDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setEditingRow(null);
          }}
          onSuccess={() => {
            setEditDialogOpen(false);
            setEditingRow(null);
            setTimeout(() => {
              loadRectorComments();
            }, 500);
          }}
          editingData={editingRow}
          terms={terms || []}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {isAdmin && deleteDialogOpen && (
        <DeleteDialog
          open={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setDeletingRow(null);
          }}
          onConfirm={handleDeleteConfirm}
          deletingData={deletingRow}
          isLoading={isServiceLoading}
        />
      )}
    </Box>
  );
};

export default StudentRectorComments;

